import axios, { endpoints } from "../../libs/axios";

const createOrder = async (data: any) => {
  const response = await axios.post(endpoints?.order.create, data);
  return response.data;
};

function toOrderArray(payload: any): any[] {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.docs)) return payload.docs;
  if (Array.isArray(payload?.results)) return payload.results;
  if (Array.isArray(payload?.orders)) return payload.orders;
  return [];
}

function readTotalPages(payload: any): number | null {
  const raw =
    payload?.totalPages ??
    payload?.pagination?.totalPages ??
    payload?.meta?.totalPages ??
    payload?.pages;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function dedupeOrders(rows: any[]): any[] {
  const seen = new Set<string>();
  const out: any[] = [];
  for (const row of rows) {
    const key = String(row?.orderId ?? row?._id ?? "");
    if (!key) {
      out.push(row);
      continue;
    }
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(row);
  }
  return out;
}

const getOrders = async () => {
  const limit = 100;
  let page = 1;
  let totalPages: number | null = null;
  const allOrders: any[] = [];

  while (page <= 50) {
    const response = await axios.get(endpoints?.order.myOrder, {
      params: { page, limit },
    });

    // Backend variants:
    // 1) { data: [...] }
    // 2) { data: { docs: [...], totalPages: N } }
    // 3) { data: { data: [...], totalPages: N } }
    const root = response?.data;
    const payload = root?.data ?? root;
    const pageOrders = toOrderArray(payload);
    allOrders.push(...pageOrders);

    if (totalPages == null) {
      totalPages = readTotalPages(payload) ?? readTotalPages(root);
    }

    const hasMoreByMeta =
      totalPages != null ? page < totalPages : pageOrders.length === limit;
    if (!hasMoreByMeta) break;
    page += 1;
  }

  return dedupeOrders(allOrders);
};

const createCheckoutSession = async (data: any) => {
  const response = await axios.post(endpoints?.payment?.checkout, data);
  return response.data;
};

const getNuveiSavedCards = async () => {
  const response = await axios.get(endpoints?.payment?.nuveiSavedCards);
  return response.data?.cards || [];
};

const saveNuveiPaymentOption = async (data: {
  userPaymentOptionId: string;
  cardBrand?: string;
  cardLast4?: string;
  cardExpMonth?: string;
  cardExpYear?: string;
  card?: {
    maskedCardNumber?: string;
    cardBrand?: string;
    expirationMonth?: string;
    expirationYear?: string;
    last4Digits?: string;
  };
}) => {
  const response = await axios.post(
    endpoints?.payment?.nuveiSavePaymentOption,
    data,
  );
  return response.data;
};

const applyCoupon = async (couponCode: string) => {
  const response = await axios.post(endpoints?.order.applyCoupon, couponCode);
  return response.data;
};

const getShippingRates = async (data: any) => {
  const response = await axios.get(
    `${endpoints?.order.shippingRates}?subTotal=${data?.subTotal}&state=${data?.shippingState}`,
  );
  return response.data;
};

const getAddons = async () => {
  const response = await axios.get(endpoints?.order.addons);
  return response.data;
};

function parseRushOrderPrice(data: any): number | null {
  const toNum = (v: any): number | null => {
    if (v == null) return null;
    const n = typeof v === "number" ? v : Number(v);
    return Number.isNaN(n) ? null : n;
  };
  if (data == null) return null;
  const direct = toNum(data);
  if (direct != null) return direct;
  const n = toNum(data?.price ?? data?.amount ?? data?.fee);
  if (n != null) return n;
  const inner = data?.data;
  const innerNum = toNum(inner);
  if (innerNum != null) return innerNum;
  const innerN = toNum(inner?.price ?? inner?.amount ?? inner?.fee);
  if (innerN != null) return innerN;
  const arr = Array.isArray(inner) ? inner : Array.isArray(data) ? data : null;
  const first = arr?.[0];
  if (first && typeof first === "object") {
    const firstN = toNum(first?.price ?? first?.amount ?? first?.fee);
    if (firstN != null) return firstN;
  }
  return null;
}

const getRushOrderPrice = async (): Promise<number | null> => {
  try {
    const response = await axios.get(endpoints?.order.rushOrder);
    return parseRushOrderPrice(response?.data);
  } catch {
    return null;
  }
};

export {
  applyCoupon,
  createCheckoutSession,
  createOrder,
  getAddons,
  getNuveiSavedCards,
  getOrders,
  getRushOrderPrice,
  getShippingRates,
  saveNuveiPaymentOption,
};
